import { 
  Subsystem, 
  SubsystemElement, 
  PlacedBlock, 
  SocketConnection,
  PlacedLabel,
  TextAnnotation,
  IsoPoint 
} from '@/app/types/cad';
import { generateSubsystemPreview } from '@/app/utils/subsystemPreview';

/**
 * Creates a subsystem from selected elements
 * @param selectedBlocks - Selected blocks to include
 * @param selectedConnections - Selected connections to include
 * @param selectedLabels - Selected labels to include
 * @param selectedTexts - Selected text annotations to include
 * @param name - Name for the subsystem
 * @param author - Optional author name
 * @returns A new Subsystem object
 */
export function createSubsystemFromSelection(
  selectedBlocks: PlacedBlock[],
  selectedConnections: SocketConnection[],
  selectedLabels: PlacedLabel[],
  selectedTexts: TextAnnotation[],
  name: string,
  author?: string
): Subsystem {
  // Calculate the center point (average position of all elements)
  const centerPoint = calculateCenterPoint(selectedBlocks, selectedConnections, selectedLabels, selectedTexts);

  // Create subsystem elements with relative positions - ENSURE IT'S AN ARRAY
  const elements: SubsystemElement[] = [];

  // Add blocks
  selectedBlocks.forEach(block => {
    if (block.transform?.position) {
      elements.push({
        type: 'block',
        id: block.id,
        relativePosition: {
          u: block.transform.position.u - centerPoint.u,
          v: block.transform.position.v - centerPoint.v,
        },
        data: block,
      });
    }
  });

  // Add connections
  selectedConnections.forEach(conn => {
    if (conn.position) {
      elements.push({
        type: 'connection',
        id: conn.id,
        relativePosition: {
          u: conn.position.u - centerPoint.u,
          v: conn.position.v - centerPoint.v,
        },
        data: conn,
      });
    }
  });

  // Add labels
  selectedLabels.forEach(label => {
    if (label.position) {
      elements.push({
        type: 'label',
        id: label.id,
        relativePosition: {
          u: label.position.u - centerPoint.u,
          v: label.position.v - centerPoint.v,
        },
        data: label,
      });
    }
  });

  // Add text annotations
  selectedTexts.forEach(text => {
    if (text.position) {
      elements.push({
        type: 'text-annotation',
        id: text.id,
        relativePosition: {
          u: text.position.u - centerPoint.u,
          v: text.position.v - centerPoint.v,
        },
        data: text,
      });
    }
  });

  console.log('📦 Creating subsystem with', elements.length, 'elements (Array?', Array.isArray(elements), ')');

  const now = new Date().toISOString();

  const subsystem: Subsystem = {
    id: `subsystem-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    name,
    version: '1.0',
    created: now,
    modified: now,
    elements, // This is guaranteed to be an array
    centerPoint,
    author,
    favorite: false,
    containsSubsystems: false,
  };

  // Validate before generating preview
  if (!Array.isArray(subsystem.elements)) {
    console.error('❌ ERROR: subsystem.elements is not an array!', typeof subsystem.elements, subsystem.elements);
    throw new Error('Failed to create subsystem: elements is not an array');
  }

  // Generate preview (with error handling)
  try {
    subsystem.preview = generateSubsystemPreview(subsystem);
  } catch (error) {
    console.warn('⚠️ Failed to generate subsystem preview:', error);
    subsystem.preview = ''; // Set empty preview on error
  }

  return subsystem;
}

/**
 * Calculate the geometric center of all selected elements
 */
function calculateCenterPoint(
  blocks: PlacedBlock[],
  connections: SocketConnection[],
  labels: PlacedLabel[],
  texts: TextAnnotation[]
): IsoPoint {
  const allPositions: IsoPoint[] = [];

  // Safely extract positions, filtering out any undefined values
  blocks.forEach(b => {
    if (b.transform?.position) {
      allPositions.push(b.transform.position);
    }
  });
  
  connections.forEach(c => {
    if (c.position) {
      allPositions.push(c.position);
    }
  });
  
  labels.forEach(l => {
    if (l.position) {
      allPositions.push(l.position);
    }
  });
  
  texts.forEach(t => {
    if (t.position) {
      allPositions.push(t.position);
    }
  });

  if (allPositions.length === 0) {
    return { u: 0, v: 0 };
  }

  const sumU = allPositions.reduce((sum, pos) => sum + (pos?.u || 0), 0);
  const sumV = allPositions.reduce((sum, pos) => sum + (pos?.v || 0), 0);

  return {
    u: sumU / allPositions.length,
    v: sumV / allPositions.length,
  };
}

/**
 * Save subsystem to localStorage library
 */
export function saveSubsystemToLibrary(subsystem: Subsystem): void {
  const STORAGE_KEY = 'subsystem-library';
  
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const library: Subsystem[] = stored ? JSON.parse(stored) : [];
    
    // Add new subsystem to library
    library.push(subsystem);
    
    // Save back to localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify(library));
    
    // Trigger update event so SubsystemLibraryPanel refreshes
    window.dispatchEvent(new Event('subsystem-library-updated'));
  } catch (error) {
    console.error('Error saving subsystem to library:', error);
    throw error;
  }
}